import classNames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import ShareButton from '../Buttons/ShareButton';
import SemanticHeader from '../Chapter/semantic-header';
import InfoBox from './infoBox';
import QuizQuestion from './quizQuestion';
import useWindowLocationHref from '../Hooks/useWindowLocation';

const QuizContainerBlock = ({
  heading,
  introText,
  link,
  linkText,
  internalLinkName,
  questions,
  answerSpans,
  blockId,
  chapterContainerHasHeading,
  isCollectionPage,
  shareButton,
}) => {
  const href = useWindowLocationHref();
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [quizInProgress, setQuizInProgress] = useState(true);
  const [correctNumberOfQuestions, setCorrectNumberOfQuestions] = useState(0);
  const [finishedQuizText, setFinishedQuizText] = useState('');
  const [hideQuizBlock, setHideQuizBlock] = useState(false);
  const quizResultsRef = useRef(null);

  const currentQuestion = questions?.[currentQuestionIndex];

  const nextPaginationButtonActive =
    currentQuestionIndex === 0
      ? Object.keys(answeredQuestions).length > 0
      : currentQuestionIndex <= Object.keys(answeredQuestions).length - 1;

  const handleQuestionAnswer = (questionId, answer) => {
    setAnsweredQuestions({
      ...answeredQuestions,
      [questionId]: answer,
    });
  };

  const handleNext = () => {
    if (currentQuestionIndex < questions?.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      scrollToTop();
    } else {
      setQuizInProgress(false);
    }
  };

  const handlePrev = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      scrollToTop();
    }
  };

  const scrollToTop = () => {
    requestAnimationFrame(() => {
      const heading = document.getElementById(`quiz-heading-${blockId}`);
      if (heading) {
        const headingTop = heading.getBoundingClientRect().top + window.scrollY;
        window.scrollTo({ top: headingTop + -150 });
        heading.focus();
      }
    });
  };

  const handleHideQuiz = () => {
    setHideQuizBlock(!hideQuizBlock);
    scrollToTop();
  };

  useEffect(() => {
    const correctAnswersCount = Object.values(answeredQuestions).filter(
      (question) => question.IsCorrect
    ).length;
    setCorrectNumberOfQuestions(correctAnswersCount);
    const answerSpan = answerSpans
      ?.sort((a, b) => a.Max - b.Max)
      ?.find((answer) => correctAnswersCount < answer.Max);
    setFinishedQuizText(answerSpan?.Text);
  }, [answeredQuestions]);

  const updateQuizResultHeight = () => {
    if (quizResultsRef.current) {
      const currentContentHeight = hideQuizBlock
        ? 0
        : quizResultsRef.current.scrollHeight;
      quizResultsRef.current.style.maxHeight = `${currentContentHeight}px`;
    }
  };

  useEffect(() => {
    updateQuizResultHeight();
  }, [hideQuizBlock, quizInProgress]);

  if (!questions || questions.length === 0) {
    return null;
  }

  return (
    <div
      className={classNames('c-quiz-container', {
        'c-quiz-container--collection-page': isCollectionPage,
      })}
    >
      <div className="c-quiz-container__inner">
        <SemanticHeader
          isH3={chapterContainerHasHeading}
          className="c-quiz-container__heading"
          id={`quiz-heading-${blockId}`}
          tabIndex={-1}
        >
          {heading}
        </SemanticHeader>
        <p className="c-quiz-container__intro">{introText}</p>
        <InfoBox
          quizInProgress={quizInProgress}
          currentQuestionNr={currentQuestionIndex + 1}
          nrOfQuestions={questions.length}
          correctNrOfQuestions={correctNumberOfQuestions}
          finishedQuizText={finishedQuizText}
          link={link}
          linkText={linkText}
          friendlyLinkName={internalLinkName}
        />
        {quizInProgress ? (
          currentQuestion ? (
            <QuizQuestion
              questionText={currentQuestion.QuestionText}
              answerAlternatives={currentQuestion.AnswerAlternatives}
              image={currentQuestion.Image?.[0]}
              onAnswer={(answer) =>
                handleQuestionAnswer(currentQuestion.BlockId, answer)
              }
              selectedAnswer={answeredQuestions[currentQuestion.BlockId]}
              blockId={currentQuestion.BlockId}
              correctFeedbackText={currentQuestion.CorrectFeedbackText}
              incorrectFeedbackText={currentQuestion.IncorrectFeedbackText}
              correctAnswerLink={currentQuestion.CorrectAnswerLink}
              correctAnswerLinkText={currentQuestion.CorrectAnswerLinkText}
              friendlyCorrectLinkName={currentQuestion.FriendlyCorrectLinkName}
              incorrectAnswerLink={currentQuestion.IncorrectAnswerLink}
              incorrectAnswerLinkText={currentQuestion.IncorrectAnswerLinkText}
              friendlyIncorrectLinkName={
                currentQuestion.FriendlyIncorrectLinkName
              }
              quizInProgress={quizInProgress}
              chapterContainerHasHeading={chapterContainerHasHeading}
            />
          ) : null
        ) : (
          <div className="c-quiz-result">
            <div
              ref={quizResultsRef}
              id={`quiz-result-${blockId}`}
              className={`c-quiz-result__questions ${
                hideQuizBlock ? 'hidden' : 'visible'
              }`}
            >
              {questions.map((question, i) =>
                question ? (
                  <div key={question.BlockId}>
                    <QuizQuestion
                      questionText={question.QuestionText}
                      answerAlternatives={question.AnswerAlternatives}
                      image={question.Image?.[0]}
                      onAnswer={(answer) =>
                        handleQuestionAnswer(question.BlockId, answer)
                      }
                      selectedAnswer={answeredQuestions[question.BlockId]}
                      blockId={question.BlockId}
                      correctFeedbackText={question.CorrectFeedbackText}
                      incorrectFeedbackText={question.IncorrectFeedbackText}
                      correctAnswerLink={question.CorrectAnswerLink}
                      correctAnswerLinkText={question.CorrectAnswerLinkText}
                      friendlyCorrectLinkName={question.FriendlyCorrectLinkName}
                      incorrectAnswerLink={question.IncorrectAnswerLink}
                      incorrectAnswerLinkText={question.IncorrectAnswerLinkText}
                      friendlyIncorrectLinkName={
                        question.FriendlyIncorrectLinkName
                      }
                      quizInProgress={quizInProgress}
                      currentAnswerIndex={i + 1}
                    />
                  </div>
                ) : null
              )}
              {shareButton && (
                <div className="c-quiz-result__share-container">
                  <ShareButton
                    description={shareButton.ShareDescription}
                    buttonText={shareButton.ShareButtonText}
                    link={shareButton.ShareButtonLink}
                    title={shareButton.ShareTitle}
                    copiedToClipboardMessage={'Länken blev kopierad'}
                  />
                </div>
              )}
            </div>

            <div className="c-quiz-result__show-toggle">
              <button
                onClick={() => handleHideQuiz()}
                className={`c-quiz-result__show-toggle-btn ${
                  hideQuizBlock ? 'closed' : 'open'
                }`}
                aria-expanded={
                  currentQuestionIndex === questions.length - 1
                    ? quizInProgress
                      ? 'true'
                      : 'false'
                    : undefined
                }
                aria-controls={`quiz-result-${blockId}`}
              >
                {hideQuizBlock ? 'Visa mitt resultat' : 'Dölj mitt resultat'}
              </button>
            </div>
          </div>
        )}
        {quizInProgress && (
          <div className="c-quiz-pagination">
            <button
              aria-label={
                currentQuestionIndex === 0 ? '' : `Gå till föregående fråga`
              }
              type="button"
              onClick={handlePrev}
              className="c-quiz-pagination__action c-quiz-pagination__action--prev"
              style={{
                visibility: currentQuestionIndex === 0 ? 'hidden' : 'visible',
              }}
            >
              Föregående fråga
            </button>
            <button
              data-event-category={href || 'novalue'}
              data-event-action={heading}
              data-event-name="Nästa fråga"
              data-event-value={currentQuestionIndex}
              type="button"
              onClick={handleNext}
              disabled={!nextPaginationButtonActive}
              aria-disabled={!nextPaginationButtonActive}
              className="c-quiz-pagination__action c-quiz-pagination__action--next"
              aria-label={
                currentQuestionIndex === questions.length - 1
                  ? 'Se ditt resultat'
                  : 'Gå till nästa fråga'
              }
            >
              {currentQuestionIndex === questions.length - 1
                ? 'Se ditt resultat'
                : 'Nästa fråga'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuizContainerBlock;
